import { useEffect, useMemo, useState, type ComponentProps } from 'react'
import { Loader2, Trash2, UserRound } from 'lucide-react'
import {
  isCurrentUserAdminOrGlobalAdminSelector,
  useGetCurrentUserQuery,
  useGetGroupsQuery,
  useGetUsersQuery
} from '@axilon-apps/domains-identity/state'
import {
  useAddResourceAccessGroupGrantMutation,
  useAddResourceAccessUserGrantMutation,
  useAppSelector,
  useGetResourceAccessQuery,
  useRemoveResourceAccessGroupGrantMutation,
  useRemoveResourceAccessUserGrantMutation,
  useTransferResourceAccessOwnerMutation,
  type ResourceAccessLevel
} from '@axilon-apps/domains-ontology-ui-state'
import { ACCESS_LEVEL_LABELS, DEFAULT_ACCESS_LEVELS } from './access-levels'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

function personName(user: { name: string; username: string } | undefined): string {
  if (!user) return 'Deleted user'
  return user.name ? `${user.name} (${user.username})` : user.username
}

function ShareError({ error, resourceLabel }: { error: unknown; resourceLabel: string }) {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status?: unknown }).status
      : undefined
  const message =
    status === 403
      ? `You no longer have permission to change sharing for this ${resourceLabel}.`
      : 'Couldn’t update sharing. Please try again.'
  return <p className="text-sm text-st-down">{message}</p>
}

function AccessLevelSelect({
  value,
  onChange,
  disabled,
  allowedAccessLevels
}: {
  value: ResourceAccessLevel
  onChange: (value: ResourceAccessLevel) => void
  disabled?: boolean
  allowedAccessLevels: readonly ResourceAccessLevel[]
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger size="sm" className="w-28">
        <SelectValue />
      </SelectTrigger>
      <ShareSelectContent>
        {allowedAccessLevels.map((accessLevel) => (
          <SelectItem key={accessLevel} value={accessLevel}>
            {ACCESS_LEVEL_LABELS[accessLevel]}
          </SelectItem>
        ))}
      </ShareSelectContent>
    </Select>
  )
}

/** Popper: item-aligned Select expands on scroll and pins to the viewport top. */
function ShareSelectContent(props: ComponentProps<typeof SelectContent>) {
  return <SelectContent {...props} position="popper" align="start" />
}

export interface ResourceSharingDialogProps {
  /** Human-readable item name interpolated into the dialog copy. */
  resourceName: string
  /** Singular resource type used in heading and error copy. */
  resourceLabel: string
  /** Proxy-issued access scope attached to the resource. */
  scopeId: string
  /** Optional resource-specific explanation shown below the dialog title. */
  scopeDescription?: string
  /** Access levels this resource supports. Defaults to Viewer, Editor, and Co-owner. */
  allowedAccessLevels?: readonly ResourceAccessLevel[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Resource-independent grant editor. Resource screens own their Share
 * affordance and authorization gate; this dialog only needs an access-scope
 * ID and display copy, so agent configs and conversations can reuse it.
 */
export function ResourceSharingDialog({
  resourceName,
  resourceLabel,
  scopeId,
  scopeDescription,
  allowedAccessLevels = DEFAULT_ACCESS_LEVELS,
  open,
  onOpenChange
}: ResourceSharingDialogProps) {
  const canShareToAnyGroup = useAppSelector(isCurrentUserAdminOrGlobalAdminSelector)
  const scope = useGetResourceAccessQuery(scopeId, { skip: !open || scopeId === '' })
  const currentUser = useGetCurrentUserQuery(undefined, { skip: !open })
  const users = useGetUsersQuery({ page: 1, perPage: 100 }, { skip: !open })
  const currentUserId = currentUser.data?.id
  // Co-owners manage sharing but only the owner (or an admin) hands it over.
  const canTransfer =
    canShareToAnyGroup ||
    (currentUserId !== undefined && scope.data?.scope.ownerUserId === currentUserId)
  const groups = useGetGroupsQuery(
    {
      page: 1,
      perPage: 100,
      ...(canShareToAnyGroup || !currentUserId ? {} : { userId: currentUserId })
    },
    { skip: !open || (!canShareToAnyGroup && !currentUserId) }
  )
  const [addUser] = useAddResourceAccessUserGrantMutation()
  const [removeUser] = useRemoveResourceAccessUserGrantMutation()
  const [addGroup] = useAddResourceAccessGroupGrantMutation()
  const [removeGroup] = useRemoveResourceAccessGroupGrantMutation()
  const [transferOwner] = useTransferResourceAccessOwnerMutation()
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [selectedOwnerId, setSelectedOwnerId] = useState('')
  const [newUserLevel, setNewUserLevel] = useState<ResourceAccessLevel>('viewer')
  const [newGroupLevel, setNewGroupLevel] = useState<ResourceAccessLevel>('viewer')
  const [mutationError, setMutationError] = useState<unknown>()
  const [isUpdating, setIsUpdating] = useState(false)
  const [confirmTransfer, setConfirmTransfer] = useState(false)
  const viewerOnly = allowedAccessLevels.length === 1 && allowedAccessLevels[0] === 'viewer'

  useEffect(() => {
    if (!open) return
    setSelectedUserId('')
    setSelectedGroupId('')
    setSelectedOwnerId('')
    setMutationError(undefined)
  }, [open, scopeId])

  const usersById = useMemo(
    () => new Map((users.data?.items ?? []).map((user) => [user.id, user])),
    [users.data?.items]
  )
  const groupsById = useMemo(
    () => new Map((groups.data?.items ?? []).map((group) => [group.id, group])),
    [groups.data?.items]
  )
  const userForId = (userId: string) =>
    usersById.get(userId) ?? (currentUser.data?.id === userId ? currentUser.data : undefined)
  const availableUsers = useMemo(() => {
    const granted = new Set((scope.data?.userGrants ?? []).map((grant) => grant.userId))
    return (users.data?.items ?? []).filter((user) => !granted.has(user.id))
  }, [scope.data?.userGrants, users.data?.items])
  const availableGroups = useMemo(() => {
    const granted = new Set((scope.data?.groupGrants ?? []).map((grant) => grant.groupId))
    return (groups.data?.items ?? []).filter((group) => !granted.has(group.id))
  }, [scope.data?.groupGrants, groups.data?.items])
  const transferCandidates = useMemo(
    () =>
      (users.data?.items ?? []).filter(
        (user) => user.enabled && user.id !== scope.data?.scope.ownerUserId
      ),
    [scope.data?.scope.ownerUserId, users.data?.items]
  )

  const update = async (operation: () => Promise<unknown>) => {
    setMutationError(undefined)
    setIsUpdating(true)
    try {
      await operation()
    } catch (error) {
      setMutationError(error)
    } finally {
      setIsUpdating(false)
    }
  }

  const addSelectedUser = () => {
    if (!selectedUserId || !scopeId) return
    const accessLevel = allowedAccessLevels.includes(newUserLevel)
      ? newUserLevel
      : (allowedAccessLevels[0] ?? 'viewer')
    void update(async () => {
      await addUser({
        scopeId,
        grant: { userId: selectedUserId, accessLevel }
      }).unwrap()
      setSelectedUserId('')
    })
  }

  const addSelectedGroup = () => {
    if (!selectedGroupId || !scopeId) return
    const accessLevel = allowedAccessLevels.includes(newGroupLevel)
      ? newGroupLevel
      : (allowedAccessLevels[0] ?? 'viewer')
    void update(async () => {
      await addGroup({
        scopeId,
        grant: { groupId: selectedGroupId, accessLevel }
      }).unwrap()
      setSelectedGroupId('')
    })
  }

  const confirmOwnershipTransfer = () => {
    if (!selectedOwnerId || !scopeId) return
    void update(async () => {
      await transferOwner({ scopeId, newOwnerUserId: selectedOwnerId }).unwrap()
      setSelectedOwnerId('')
      setConfirmTransfer(false)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Keep overflow visible so the person/group pickers are not clipped
        // by a scrollport and can use the viewport as their collision box.
        // Grant lists below scroll on their own.
        className="max-h-[calc(100vh-2rem)] overflow-visible sm:max-w-2xl"
        // The dialog may be launched from a clickable resource row. React
        // portal events still bubble through that row, so isolate all dialog
        // interactions from its select/navigation handler.
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Share {resourceLabel}</DialogTitle>
          <DialogDescription>
            Choose who can {viewerOnly ? 'view ' : 'view or edit '}
            <span className="font-medium text-foreground">{resourceName}</span>.
            {scopeDescription ? ` ${scopeDescription}` : ''}
            {allowedAccessLevels.includes('co_owner')
              ? ' Co-owners also manage sharing and get what needs attention on it.'
              : ''}
          </DialogDescription>
        </DialogHeader>

        {scope.isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-ink-3">
            <Loader2 className="size-4 animate-spin" /> Loading sharing settings…
          </div>
        ) : scope.error ? (
          <ShareError error={scope.error} resourceLabel={resourceLabel} />
        ) : !scope.data ? (
          <p className="text-sm text-ink-3">Sharing is not available for this {resourceLabel}.</p>
        ) : (
          <div className="flex flex-col gap-6">
            <section className="min-w-0 space-y-3" aria-labelledby="shared-users-heading">
              <div>
                <h3 id="shared-users-heading" className="text-sm font-semibold">
                  People
                </h3>
                <p className="text-xs text-ink-3">Direct access for individual users.</p>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border">
                {scope.data.userGrants.length === 0 ? (
                  <p className="p-3 text-sm text-ink-3">No people have direct access.</p>
                ) : (
                  scope.data.userGrants.map((grant) => (
                    <div
                      key={grant.userId}
                      className="flex items-center gap-2 border-b p-2 last:border-b-0"
                    >
                      <UserRound className="size-4 shrink-0 text-ink-3" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {personName(userForId(grant.userId))}
                      </span>
                      <AccessLevelSelect
                        value={grant.accessLevel}
                        disabled={isUpdating}
                        allowedAccessLevels={allowedAccessLevels}
                        onChange={(accessLevel) =>
                          void update(() =>
                            addUser({ scopeId, grant: { ...grant, accessLevel } }).unwrap()
                          )
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Remove ${personName(userForId(grant.userId))}`}
                        disabled={isUpdating}
                        onClick={() =>
                          void update(() => removeUser({ scopeId, userId: grant.userId }).unwrap())
                        }
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="min-w-0 flex-1" aria-label="Add a person">
                    <SelectValue placeholder="Add a person" />
                  </SelectTrigger>
                  <ShareSelectContent>
                    {availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {personName(user)}
                      </SelectItem>
                    ))}
                  </ShareSelectContent>
                </Select>
                <AccessLevelSelect
                  value={newUserLevel}
                  onChange={setNewUserLevel}
                  allowedAccessLevels={allowedAccessLevels}
                />
                <Button
                  size="sm"
                  disabled={!selectedUserId || isUpdating}
                  onClick={addSelectedUser}
                >
                  Add
                </Button>
              </div>
            </section>

            <section className="min-w-0 space-y-3" aria-labelledby="shared-groups-heading">
              <div>
                <h3 id="shared-groups-heading" className="text-sm font-semibold">
                  Groups
                </h3>
                <p className="text-xs text-ink-3">Members receive access through their group.</p>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border">
                {scope.data.groupGrants.length === 0 ? (
                  <p className="p-3 text-sm text-ink-3">No groups have access.</p>
                ) : (
                  scope.data.groupGrants.map((grant) => (
                    <div
                      key={grant.groupId}
                      className="flex items-center gap-2 border-b p-2 last:border-b-0"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {groupsById.get(grant.groupId)?.name ?? `Deleted group (${grant.groupId})`}
                      </span>
                      <AccessLevelSelect
                        value={grant.accessLevel}
                        disabled={isUpdating}
                        allowedAccessLevels={allowedAccessLevels}
                        onChange={(accessLevel) =>
                          void update(() =>
                            addGroup({ scopeId, grant: { ...grant, accessLevel } }).unwrap()
                          )
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Remove ${groupsById.get(grant.groupId)?.name ?? grant.groupId}`}
                        disabled={isUpdating}
                        onClick={() =>
                          void update(() =>
                            removeGroup({ scopeId, groupId: grant.groupId }).unwrap()
                          )
                        }
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger className="min-w-0 flex-1" aria-label="Add a group">
                    <SelectValue placeholder="Add a group" />
                  </SelectTrigger>
                  <ShareSelectContent>
                    {availableGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </ShareSelectContent>
                </Select>
                <AccessLevelSelect
                  value={newGroupLevel}
                  onChange={setNewGroupLevel}
                  allowedAccessLevels={allowedAccessLevels}
                />
                <Button
                  size="sm"
                  disabled={!selectedGroupId || isUpdating}
                  onClick={addSelectedGroup}
                >
                  Add
                </Button>
              </div>
            </section>

            <section
              className="min-w-0 space-y-3 border-t pt-6"
              aria-labelledby="ownership-heading"
            >
              <div>
                <h3 id="ownership-heading" className="text-sm font-semibold">
                  Ownership
                </h3>
                <p className="text-xs text-ink-3">
                  Current owner:{' '}
                  <span title={scope.data.scope.ownerUserId}>
                    {personName(userForId(scope.data.scope.ownerUserId ?? ''))}
                  </span>
                </p>
              </div>
              {canTransfer ? (
                <div className="flex gap-2">
                  <Select value={selectedOwnerId} onValueChange={setSelectedOwnerId}>
                    <SelectTrigger className="min-w-0 flex-1" aria-label="Select a new owner">
                      <SelectValue placeholder="Select a new owner" />
                    </SelectTrigger>
                    <ShareSelectContent>
                      {transferCandidates.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {personName(user)}
                        </SelectItem>
                      ))}
                    </ShareSelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    disabled={!selectedOwnerId || isUpdating}
                    onClick={() => setConfirmTransfer(true)}
                  >
                    Transfer ownership
                  </Button>
                </div>
              ) : null}
            </section>
          </div>
        )}

        {mutationError ? <ShareError error={mutationError} resourceLabel={resourceLabel} /> : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmTransfer} onOpenChange={setConfirmTransfer}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer ownership?</AlertDialogTitle>
            <AlertDialogDescription>
              The current owner immediately loses owner access to this {resourceLabel} unless they
              retain access through a group or direct grant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOwnershipTransfer}>
              Transfer ownership
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
